// Lock… / Unlock from a file's menu.
//
// Locking takes a reason (shown to everyone who opens the file) and freezes
// the file for everyone, the locker too, until someone who may unlock does:
// the locker, a manager or the owner. The current version is kept as it is
// (PUT /embed/v1/file/{file_uuid}/lock); unlocking is DELETE on the same route.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, LockKeyhole, LockKeyholeOpen } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDeleteRequest, usePutRequest } from '@/shared/hooks/requests.js';
import { fileStateText, lockErrorMessage } from '@/shared/utils/fileStateText.js';
import { isLockedByMe, lockOf } from '@/shared/utils/nodeAccess.js';
import { FILE_LOCK } from '@/shared/utils/urls.js';

export const LOCK_REASON_MAX = 200;

/**
 * @param {object|null} file  the file row (null closes the dialog)
 * @param {'lock'|'unlock'} mode
 * @param {function} onOpenChange
 * @param {function} onDone   called with the updated file read
 */
export function LockFileDialog({ file, mode = 'lock', onOpenChange, onDone }) {
  const { t } = useTranslation(['my-docs', 'editor', 'errors', 'common']);
  const [reason, setReason] = useState('');
  const url = file?.uuid ? FILE_LOCK.replace('{file_uuid}', file.uuid) : null;
  const lockRequest = usePutRequest({ showError: false });
  const unlockRequest = useDeleteRequest({ showError: false });
  const busy = lockRequest.loading || unlockRequest.loading;
  const lock = lockOf(file);
  const unlocking = mode === 'unlock';

  useEffect(() => {
    if (file) setReason('');
  }, [file?.uuid, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!url) return;
    const res = unlocking
      ? await unlockRequest.request({ url })
      : await lockRequest.request({ url, data: { reason: reason.trim(), ref: null } });
    if (!res.success) {
      toast.error(lockErrorMessage(t, res.errorInfo));
      return;
    }
    toast.success(t(unlocking ? 'my-docs:lock.unlocked' : 'my-docs:lock.locked'));
    onOpenChange(false);
    onDone?.(res.response);
  };

  const trimmed = reason.trim();
  return (
    <Dialog open={Boolean(file)} onOpenChange={(open) => { if (!open && !busy) onOpenChange(false); }}>
      <DialogContent className="sm:max-w-md" onClick={(e) => e.stopPropagation()} data-testid="lock-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            {unlocking ? <LockKeyholeOpen className="h-4 w-4" /> : <LockKeyhole className="h-4 w-4" />}
            {t(unlocking ? 'my-docs:lock.unlockTitle' : 'my-docs:lock.title', { name: file?.name || '' })}
          </DialogTitle>
          <DialogDescription>
            {unlocking
              ? `${lock ? fileStateText(t, 'locked', lock, { mine: isLockedByMe(file) }) : ''} ${t(isLockedByMe(file) ? 'my-docs:lock.unlockHint' : 'my-docs:lock.unlockOthersHint')}`.trim()
              : t('my-docs:lock.hint')}
          </DialogDescription>
        </DialogHeader>
        {!unlocking && (
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground" htmlFor="lock-reason">
              {t('my-docs:lock.reasonLabel')}
            </label>
            <Textarea
              id="lock-reason"
              data-testid="lock-reason"
              value={reason}
              maxLength={LOCK_REASON_MAX}
              rows={2}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('my-docs:lock.reasonPlaceholder')}
            />
            <p className="text-right text-[10px] text-muted-foreground">{reason.length}/{LOCK_REASON_MAX}</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            {t('common:cancel')}
          </Button>
          <Button onClick={submit} disabled={busy || (!unlocking && !trimmed)} data-testid="lock-confirm">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t(unlocking ? 'my-docs:lock.unlockConfirm' : 'my-docs:lock.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
